import { fetchConfig } from '@theme/utilities';
import { CartAddEvent } from '@theme/events';

/**
 * "You may also like" carousel inside the cart drawer.
 *
 * Fetches `sections/cart-drawer-recommendations.liquid` through the product
 * recommendations endpoint (so Liquid does the money formatting and filters out
 * products already in the cart) and swaps the HTML in.
 *
 * The element carries `data-skip-subtree-update`, so the cart section morph only
 * refreshes its attributes. `data-cart-key` changes whenever the cart contents
 * change, which triggers a reload here.
 *
 * Source: Shopify Product Recommendations (`intent=related`) seeded by the first
 * cart line. Liquid then drops products already in the cart and keeps 6.
 */
class CartDrawerRecommendations extends HTMLElement {
  static observedAttributes = ['data-product-id', 'data-cart-key'];

  static #AUTOPLAY_MS = 4000;

  /** @type {AbortController | null} */
  #activeFetch = null;

  #loadScheduled = false;

  /** @type {number} */
  #autoplayTimer = 0;

  #paused = false;

  /** @type {HTMLDialogElement | null} */
  #dialog = null;

  /** @type {Element | null} */
  #drawer = null;

  connectedCallback() {
    this.addEventListener('click', this.#onClick);
    this.addEventListener('mouseenter', this.#pause);
    this.addEventListener('mouseleave', this.#resume);
    this.addEventListener('focusin', this.#pause);
    this.addEventListener('focusout', this.#onFocusOut);
    document.addEventListener('visibilitychange', this.#onVisibility);
    this.#dialog = this.closest('dialog');
    this.#drawer = this.closest('cart-drawer-component');
    this.#drawer?.addEventListener('dialog:open', this.#onDialogToggle);
    this.#drawer?.addEventListener('dialog:close', this.#onDialogToggle);
    this.#scheduleLoad();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
    this.removeEventListener('mouseenter', this.#pause);
    this.removeEventListener('mouseleave', this.#resume);
    this.removeEventListener('focusin', this.#pause);
    this.removeEventListener('focusout', this.#onFocusOut);
    document.removeEventListener('visibilitychange', this.#onVisibility);
    this.#drawer?.removeEventListener('dialog:open', this.#onDialogToggle);
    this.#drawer?.removeEventListener('dialog:close', this.#onDialogToggle);
    this.#drawer = null;
    this.#dialog = null;
    this.#stopAutoplay();
    this.#activeFetch?.abort();
  }

  /**
   * @param {string} _name
   * @param {string | null} oldValue
   * @param {string | null} newValue
   */
  attributeChangedCallback(_name, oldValue, newValue) {
    if (oldValue === null || oldValue === newValue || !this.isConnected) return;
    this.#scheduleLoad();
  }

  #scheduleLoad() {
    if (this.#loadScheduled) return;
    this.#loadScheduled = true;
    queueMicrotask(() => {
      this.#loadScheduled = false;
      this.#load();
    });
  }

  async #load() {
    const { productId, url, sectionId } = this.dataset;

    if (!productId || !url || !sectionId) {
      this.hidden = true;
      return;
    }

    const params = new URLSearchParams({
      section_id: sectionId,
      product_id: productId,
      limit: '8',
      intent: 'related',
    });

    this.#activeFetch?.abort();
    this.#activeFetch = new AbortController();

    try {
      const response = await fetch(`${url}?${params}`, { signal: this.#activeFetch.signal });
      if (!response.ok) throw new Error(`Recommendations returned ${response.status}`);

      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
      const section = doc.querySelector('.shopify-section') ?? doc.body;

      this.innerHTML = section.innerHTML;
      this.hidden = !this.querySelector('.cart-drawer-recs__item');
      this.#updateNav();
      this.#startAutoplay();
    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') return;
      console.error('Cart drawer recommendations:', error);
      this.hidden = true;
    } finally {
      this.#activeFetch = null;
    }
  }

  /** @param {Event} event */
  #onClick = (event) => {
    if (!(event.target instanceof Element)) return;

    const nav = event.target.closest('[data-recs-nav]');
    if (nav instanceof HTMLElement) {
      this.#scrollBy(nav.dataset.recsNav === 'prev' ? -1 : 1);
      return;
    }

    const add = event.target.closest('.cart-drawer-recs__add');
    if (add instanceof HTMLButtonElement) {
      this.#addToCart(add);
    }
  };

  /** @param {number} direction */
  #scrollBy(direction) {
    const list = this.querySelector('[data-recs-list]');
    if (!(list instanceof HTMLElement)) return;

    const item = list.querySelector('.cart-drawer-recs__item');
    const step = item instanceof HTMLElement ? item.offsetWidth : list.clientWidth;
    const max = list.scrollWidth - list.clientWidth;
    const target = list.scrollLeft + direction * step;

    // Instant wrap — smooth-scrolling the long way back through every card looks wrong.
    if (target > max + 1) {
      list.scrollTo({ left: 0, behavior: 'auto' });
      return;
    }
    if (target < -1) {
      list.scrollTo({ left: max, behavior: 'auto' });
      return;
    }

    list.scrollTo({ left: target, behavior: 'smooth' });
  }

  #startAutoplay() {
    this.#stopAutoplay();

    const items = this.querySelectorAll('.cart-drawer-recs__item');
    if (items.length <= 1) return;
    if (!this.#isDrawerOpen()) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    this.#autoplayTimer = window.setInterval(() => {
      if (this.#paused || document.hidden || !this.#isDrawerOpen()) return;
      this.#scrollBy(1);
    }, CartDrawerRecommendations.#AUTOPLAY_MS);
  }

  #stopAutoplay() {
    if (!this.#autoplayTimer) return;
    window.clearInterval(this.#autoplayTimer);
    this.#autoplayTimer = 0;
  }

  #isDrawerOpen() {
    return this.#dialog instanceof HTMLDialogElement && this.#dialog.open;
  }

  #pause = () => {
    this.#paused = true;
  };

  #resume = () => {
    this.#paused = false;
  };

  /** @param {FocusEvent} event */
  #onFocusOut = (event) => {
    if (event.relatedTarget instanceof Node && this.contains(event.relatedTarget)) return;
    this.#paused = false;
  };

  #onVisibility = () => {
    if (!document.hidden && this.#isDrawerOpen()) this.#paused = false;
  };

  #onDialogToggle = () => {
    if (this.#isDrawerOpen()) this.#startAutoplay();
    else this.#stopAutoplay();
  };

  #updateNav() {
    const list = this.querySelector('[data-recs-list]');
    const items = this.querySelectorAll('.cart-drawer-recs__item');
    const navs = this.querySelectorAll('[data-recs-nav]');

    if (!(list instanceof HTMLElement)) return;

    const single = items.length <= 1;
    navs.forEach((nav) => {
      if (nav instanceof HTMLElement) nav.hidden = single;
    });
  }

  /** @param {HTMLButtonElement} button */
  async #addToCart(button) {
    const { variantId, productId } = button.dataset;
    if (!variantId) return;

    const sectionIds = Array.from(document.querySelectorAll('cart-items-component'))
      .map((el) => (el instanceof HTMLElement ? el.dataset.sectionId : undefined))
      .filter(Boolean);

    const body = JSON.stringify({
      items: [{ id: Number(variantId), quantity: 1 }],
      sections: sectionIds.join(','),
      sections_url: window.location.pathname,
    });

    button.disabled = true;
    button.classList.add('is-loading');

    try {
      const response = await fetch(Theme.routes.cart_add_url, fetchConfig('json', { body }));
      const data = await response.json();

      if (data.status) {
        console.error('Cart drawer recommendations add failed:', data.description || data.message);
        button.disabled = false;
        return;
      }

      // The header bubble sets its count to `itemCount` verbatim for non product-form sources,
      // so read the real total back from the freshly rendered cart section.
      let itemCount = 1;
      const firstSectionHtml = sectionIds[0] ? data.sections?.[sectionIds[0]] : undefined;
      if (firstSectionHtml) {
        const doc = new DOMParser().parseFromString(firstSectionHtml, 'text/html');
        const count = parseInt(doc.querySelector('[ref="cartItemCount"]')?.textContent ?? '', 10);
        if (!Number.isNaN(count)) itemCount = count;
      }

      this.dispatchEvent(
        new CartAddEvent({}, variantId, {
          source: 'cart-drawer-recommendations',
          itemCount,
          productId,
          sections: data.sections,
        })
      );
    } catch (error) {
      console.error(error);
      button.disabled = false;
    } finally {
      button.classList.remove('is-loading');
    }
  }
}

if (!customElements.get('cart-drawer-recommendations')) {
  customElements.define('cart-drawer-recommendations', CartDrawerRecommendations);
}
